<template>
  <div class="insp-picker">
    <!-- Master-Tag scope toggle. Default ticked so the picker pre-narrows to
         inspections on this Master Tag — the workflow's most common shape.
         Unticking widens to facility-wide search for the rare cross-tag pick. -->
    <div v-if="showScopeToggle" class="insp-picker__scope">
      <v-checkbox
        v-model="scopeToMasterTag"
        hide-details
        dense
        :disabled="disabled"
        class="insp-picker__scope-cb"
        data-test="insp-picker-scope-master-tag"
      >
        <template v-slot:label>
          <span class="insp-picker__scope-label">
            Show only inspections on this Master Tag
          </span>
        </template>
      </v-checkbox>
    </div>

    <!-- The autocomplete itself. We rely on v-autocomplete's no-filter + items
         binding so OUR debounced fetch owns the filtering server-side — the
         component never decides which items to show. -->
    <v-autocomplete
      ref="autocomplete"
      :value="value"
      :items="displayedItems"
      :loading="isLoading"
      :search-input.sync="searchText"
      :disabled="disabled"
      :rules="rules"
      :no-data-text="noDataText"
      item-text="name"
      item-value="id"
      label="Inspection"
      placeholder="Type to search inspections…"
      outlined
      dense
      hide-no-data
      no-filter
      clearable
      return-object
      :menu-props="{ offsetY: true, zIndex: 2100, maxHeight: 320 }"
      class="insp-picker__autocomplete"
      data-test="insp-picker-autocomplete"
      @input="onSelectionChange"
      @update:search-input="onSearchInput"
    >
      <!-- Custom item slot: name on top, ISO date + CR/RL on a meta line.
           The meta line gives the user enough to disambiguate two inspections
           with the same name (common when seeding from import). -->
      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title class="insp-picker__item-name">
            {{ item.name || '(unnamed)' }}
          </v-list-item-title>
          <v-list-item-subtitle class="insp-picker__item-meta">
            <span v-if="item.ispDate" class="insp-picker__item-date">
              {{ formatDate(item.ispDate) }}
            </span>
            <span v-else class="insp-picker__item-date insp-picker__item-date--missing">
              no date
            </span>
            <template v-if="item.cr != null">
              <span class="insp-picker__item-sep">·</span>
              <span class="insp-picker__item-metric">CR {{ formatDecimal(item.cr, 2) }}</span>
            </template>
            <template v-if="item.rl != null">
              <span class="insp-picker__item-sep">·</span>
              <span class="insp-picker__item-metric">RL {{ formatDecimal(item.rl, 1) }}</span>
            </template>
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>

      <!-- Selection slot — the picked item's chip in the input. Keeps the
           date alongside the name so the user can confirm at a glance. -->
      <template v-slot:selection="{ item }">
        <span class="insp-picker__selection">
          <span class="insp-picker__selection-name">{{ item.name || '(unnamed)' }}</span>
          <span v-if="item.ispDate" class="insp-picker__selection-date">
            {{ formatDate(item.ispDate) }}
          </span>
        </span>
      </template>

      <!-- "Load more" sentinel — pinned at the bottom of the menu. Two paths
           fire fetchPage(): IntersectionObserver (preferred — scrolls into
           view loads more silently) AND a click handler (defensive — if IO
           doesn't fire inside the teleported v-autocomplete menu, the user
           can click the sentinel to load explicitly). The menu's scroll
           container may not be the viewport, so IO with default root can
           silently no-op; the click handler closes that gap without forcing
           us to reach into Vuetify's internal DOM. -->
      <template v-slot:append-item>
        <div
          v-if="hasMore"
          ref="loadMoreSentinel"
          class="insp-picker__load-more"
          :class="{ 'insp-picker__load-more--clickable': !isLoading }"
          role="button"
          tabindex="0"
          data-test="insp-picker-load-more"
          @click="onLoadMoreClick"
          @keydown.enter.prevent="onLoadMoreClick"
          @keydown.space.prevent="onLoadMoreClick"
        >
          <template v-if="isLoading">
            <v-progress-circular size="14" width="2" indeterminate />
            <span class="insp-picker__load-more-text">Loading…</span>
          </template>
          <template v-else>
            <v-icon size="14" color="primary">mdi-arrow-down-circle-outline</v-icon>
            <span class="insp-picker__load-more-text">{{ remainingCountLabel }}</span>
          </template>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import httpService from '@/utils/httpService';
import store from '@/store';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 280;

/**
 * InspectionPickerAutocomplete — debounced, lazy-paged inspection picker for
 * the ThicknessReadingAddPanel. Owns its own fetch lifecycle (search text,
 * paging, AbortController) so the parent panel only ever sees `@input` with
 * the picked InspectionMinimalInformation-like object.
 *
 * Backend endpoint: POST /api/AMIS/fetchInspectionsAdvanced — already
 * accepts MasterTagId (filtersInfo.MasterTagId line 965 in AMISController),
 * so v1 doesn't need a new backend endpoint. The response is heavier than a
 * picker needs (full ForgeInspection objects); we project client-side to
 * { id, name, ispDate, cr, rl }. A lightweight server-side projection is a
 * future cleanup, filed separately.
 *
 * Race discipline: every search-text change aborts the prior in-flight fetch
 * before issuing a new one. Page-2+ requests share the same AbortController
 * so a parent-driven reset (e.g. scope toggle) cancels both the page-1
 * fetch AND any pending pagination.
 */
export default {
  name: 'InspectionPickerAutocomplete',

  props: {
    /**
     * v-model binding: the currently-selected inspection projection
     * ({ id, name, ispDate, cr, rl }) or null when nothing is picked.
     */
    value: { type: Object, default: null },
    /**
     * Parent Master Tag ID. When `scopeToMasterTag` is true (default), the
     * picker passes this as the MasterTagId filter on the search endpoint
     * so the user sees only inspections relevant to the Master Tag they're
     * adding a reading to.
     */
    masterTagId: { type: String, default: '' },
    /** Hide Master Tag scoping when picking from a finding, which has no master-tag context. */
    showScopeToggle: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
  },

  data() {
    return {
      searchText: '',
      // The full result set we've fetched so far, ordered by relevance.
      items: [],
      // Total count from the server (may be > items.length when lazy-paging).
      totalCount: 0,
      // 0-based offset for the next fetch (= items.length for clean pagination).
      offset: 0,
      isLoading: false,
      // When true, the picker filters by parent Master Tag. Default ticked
      // for the picker's most-common shape — inspections on THIS tag.
      scopeToMasterTag: this.showScopeToggle,
      // Track the prior debounced timer so search bursts coalesce.
      debounceTimer: null,
      // AbortController for the in-flight fetch (search OR pagination).
      // Replaced on every new fetch so older calls don't write into state.
      abortController: null,
      // IntersectionObserver instance for the lazy-load sentinel. Lazy-
      // constructed because the sentinel only renders when hasMore is true.
      io: null,
    };
  },

  computed: {
    displayedItems() {
      // If a value is preselected but not present in items, prepend it so
      // v-autocomplete can resolve the chip's label. Common on edit-mode
      // panels (we currently only Add readings, but this keeps the picker
      // edit-ready for future ThicknessReadingEditPanel).
      const items = this.items.slice();
      if (this.value && this.value.id && !items.some((i) => i.id === this.value.id)) {
        items.unshift(this.value);
      }
      return items;
    },
    hasMore() {
      return this.items.length < this.totalCount;
    },
    remainingCountLabel() {
      const remaining = this.totalCount - this.items.length;
      if (remaining <= 0) return '';
      return `Scroll for ${remaining} more`;
    },
    noDataText() {
      if (this.isLoading) return 'Searching…';
      if (!this.searchText) return 'Type to search';
      return 'No inspections match';
    },
  },

  watch: {
    masterTagId() {
      // A different parent Master Tag means a different result universe.
      // Reset everything so the picker shows fresh data on next open.
      this.resetAndFetch();
    },
    scopeToMasterTag() {
      // Toggling scope changes the result universe — same as masterTagId.
      this.resetAndFetch();
    },
    showScopeToggle() {
      this.scopeToMasterTag = this.showScopeToggle;
      this.resetAndFetch();
    },
  },

  mounted() {
    // Prime the picker with an empty-query page-1 so the menu has results
    // the moment the user opens it. Without this, the first menu open
    // shows "Type to search" even when scope filtering would already
    // produce useful results.
    this.resetAndFetch();
    this.setupIntersectionObserver();
  },

  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.abortController) this.abortController.abort();
    if (this.io) this.io.disconnect();
  },

  methods: {
    onSearchInput(text) {
      // v-autocomplete fires this on every keystroke when no-filter is set.
      // Reset paging because the search universe changes; debounce so we
      // don't issue a fetch per keystroke.
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.offset = 0;
        this.items = [];
        this.fetchPage();
      }, DEBOUNCE_MS);
    },

    onSelectionChange(picked) {
      // `picked` is the full item (return-object) or null on clear.
      debugLog('[InspectionPicker] selection changed', picked?.id || '(null)');
      this.$emit('input', picked || null);
    },

    resetAndFetch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.offset = 0;
      this.items = [];
      this.totalCount = 0;
      this.fetchPage();
    },

    /**
     * Fetch the current page-or-search slice. Replaces the in-flight fetch
     * if one exists (search-text race) and writes results into `items`,
     * appending for pagination (offset > 0) or replacing for new search.
     */
    async fetchPage() {
      // Abort any prior in-flight call BEFORE starting a new one so the
      // older response can't overwrite the newer state.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const controller = this.abortController;

      this.isLoading = true;

      const facilityId = store.state.assetId;
      const searchText = (this.searchText || '').trim();
      const payload = {
        assetId: facilityId,
        // Backend expects base64-encoded UTF-8 search text (GeneralUtils.Base64Decode).
        // Empty string sends as null so the endpoint skips the name filter.
        searchText64: searchText ? this.base64EncodeUtf8(searchText) : null,
        searchIndex: this.offset,
        searchRange: PAGE_SIZE,
        // Master-Tag scoping is the picker's main affordance for relevance.
        masterTagId: this.showScopeToggle && this.scopeToMasterTag ? (this.masterTagId || null) : null,
        // Empty arrays / default values for the endpoint's required-but-unused
        // filter surface. (The endpoint's DTO is shared with the inspection
        // list page; defaults match the no-filter case.)
        selectedUnits: [],
        masterTypes: [],
        ids: [],
        filters: [],
        customFilters: [],
        selectedFilter: 0,
        // `Fetched` in the inspection-list page distinguishes harvested vs
        // unharvested rows. The picker wants BOTH categories — and the
        // endpoint's where-clause uses `o.IsFetched == filtersInfo.Fetched`
        // which forces one side. We pick `true` (harvested) because the
        // typical reading-source is a harvested inspection. Document this
        // as a known v1 narrowing and revisit if pickers miss results.
        fetched: true,
        orderBy: { property: 'Name', ascending: true },
        reloading: false,
      };

      try {
        const response = await httpService.post({
          url: 'api/AMIS/fetchInspectionsAdvanced',
          data: payload,
          signal: controller.signal,
        });
        // Guard: if a newer fetch replaced our controller while we were in
        // flight, discard the response. (Race scenario: rapid keystrokes
        // produce overlapping fetches; the AbortController catches most but
        // a race window remains where abort lands after the response.)
        if (controller !== this.abortController) return;

        const data = response?.data || {};
        const rows = Array.isArray(data.inspections) ? data.inspections : [];
        const projected = rows.map((r) => ({
          id: r.id,
          name: r.name,
          ispDate: r.ispDate,
          cr: r.cr,
          rl: r.rl,
        }));

        if (this.offset === 0) {
          this.items = projected;
        } else {
          this.items = this.items.concat(projected);
        }
        this.totalCount = data.totalSearchResult || 0;
        this.offset = this.items.length;
        debugLog(`[InspectionPicker] fetched offset=${this.offset - projected.length} added=${projected.length} total=${this.totalCount}`);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || /abort/i.test(err?.message || '')) {
          // Expected — newer fetch superseded this one. Don't surface.
          return;
        }
        debugError('[InspectionPicker] fetchPage failed', err);
      } finally {
        if (controller === this.abortController) {
          this.isLoading = false;
        }
      }
    },

    /**
     * IntersectionObserver wiring for the "Scroll for more" sentinel. The
     * sentinel is conditionally rendered, so we re-observe via a delegated
     * check in the IO callback. When the sentinel scrolls into view AND
     * we're not already mid-fetch AND there's more to fetch — load the
     * next page.
     */
    setupIntersectionObserver() {
      if (typeof IntersectionObserver === 'undefined') return;
      this.io = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible) return;
        if (this.isLoading || !this.hasMore) return;
        this.fetchPage();
      }, { threshold: 0.5 });
      // Re-target the sentinel each time it (re-)renders. v-autocomplete
      // recycles its menu DOM aggressively, so the ref can shift; we re-
      // bind in updated().
      this.$nextTick(() => this.targetSentinel());
    },

    targetSentinel() {
      if (!this.io) return;
      this.io.disconnect();
      const sentinel = this.$refs.loadMoreSentinel;
      if (sentinel) this.io.observe(sentinel);
    },

    /**
     * Click/keyboard fallback for the IntersectionObserver. v-autocomplete's
     * menu can teleport to a stacking context where IO never fires for the
     * default viewport root — in that case the user can click "Scroll for
     * N more" to load explicitly. The guard mirrors the IO callback so
     * double-triggering (IO + click) doesn't fire two parallel fetches.
     *
     * Stops propagation so clicking the sentinel doesn't bubble up to
     * v-list-item selection handlers Vuetify wires by default.
     */
    onLoadMoreClick(event) {
      if (event) event.stopPropagation();
      if (this.isLoading || !this.hasMore) return;
      this.fetchPage();
    },

    base64EncodeUtf8(s) {
      // btoa() chokes on non-ASCII; wrap via encodeURIComponent → bytes →
      // btoa so the search box can carry any tag name. The backend uses
      // GeneralUtils.Base64Decode → ToLower().Trim() so we don't worry
      // about casing on this side.
      try {
        return btoa(unescape(encodeURIComponent(s)));
      } catch (err) {
        return null;
      }
    },

    formatDate(iso) {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
      } catch (err) {
        return '';
      }
    },

    formatDecimal(n, digits) {
      if (n == null || Number.isNaN(Number(n))) return '—';
      return Number(n).toFixed(digits);
    },
  },

  updated() {
    // Sentinel may have re-rendered as hasMore flipped from false to true;
    // re-target the IntersectionObserver so pagination works after the
    // first page lands.
    this.targetSentinel();
  },
};
</script>

<style lang="scss" scoped>
.insp-picker {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.insp-picker__scope {
  margin-bottom: 2px;
}
.insp-picker__scope-cb ::v-deep .v-input--selection-controls__input {
  margin-right: 4px;
}
.insp-picker__scope-label {
  font-size: 12px;
  color: #4a544f;
}

.insp-picker__autocomplete ::v-deep .v-input__slot {
  min-height: 38px !important;
}
.insp-picker__autocomplete ::v-deep .v-text-field--outlined fieldset {
  border-color: #ccd3ce;
  border-radius: 4px;
}
.insp-picker__autocomplete ::v-deep .v-label {
  font-size: 12px;
}

.insp-picker__item-name {
  font-size: 13px;
  font-weight: 500;
  color: #333d39;
  line-height: 1.3;
}
.insp-picker__item-meta {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10.5px;
  color: #6f7872;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.insp-picker__item-date--missing {
  font-style: italic;
  color: #969d97;
}
.insp-picker__item-sep {
  opacity: 0.45;
}
.insp-picker__item-metric {
  letter-spacing: 0.01em;
}

.insp-picker__selection {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
}
.insp-picker__selection-name {
  font-size: 13px;
  color: #333d39;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.insp-picker__selection-date {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10.5px;
  color: #6f7872;
  white-space: nowrap;
}

.insp-picker__load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10.5px;
  color: #6f7872;
  border-top: 1px dashed #e3e7e4;
  transition: background 120ms ease;
}
.insp-picker__load-more--clickable {
  cursor: pointer;
}
.insp-picker__load-more--clickable:hover,
.insp-picker__load-more--clickable:focus-visible {
  background: #f4f7f5;
  color: #1e4d3b;
  outline: none;
}
.insp-picker__load-more-text {
  letter-spacing: 0.02em;
}
</style>
